/**
 * DataPersistence - Centralized system for saving and loading editor preferences
 * Handles custom colors, user settings, and other persistent data
 */

const STORAGE_KEY = 'blog-editor-preferences'

class DataPersistence {
  constructor() {
    this.data = this.loadData()
    this.observers = new Set()
  }

  // Load data from localStorage
  loadData() {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return this.getDefaultData()
      }
      
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          customColors: [],
          userSettings: {},
          recentColors: [],
          version: '1.0.0',
          ...parsed
        }
      }
    } catch (error) {
      console.warn('Failed to load editor preferences:', error)
    }

    return this.getDefaultData()
  }

  // Return default data structure
  getDefaultData() {
    return {
      customColors: [
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#45B7D1', // Blue
        '#96CEB4', // Green
        '#FECA57', // Yellow
        '#FF9FF3'  // Pink
      ],
      recentColors: [],
      userSettings: {
        showColorPalette: true,
        maxRecentColors: 10,
        autoSave: true
      },
      version: '1.0.0'
    }
  }

  // Save data to localStorage
  saveData() {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        // In SSR, just notify observers but don't actually save
        this.notifyObservers('data_saved', this.data)
        return true
      }
      
      const dataToSave = {
        ...this.data,
        lastModified: new Date().toISOString()
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
      this.notifyObservers('data_saved', this.data)
      return true
    } catch (error) {
      console.error('Failed to save editor preferences:', error)
      return false
    }
  }

  // Get custom colors
  getCustomColors() {
    return [...this.data.customColors]
  }

  // Add custom color
  addCustomColor(color) {
    if (!color || typeof color !== 'string') return false
    
    // Normalize color format
    const normalizedColor = this.normalizeColor(color)
    if (!normalizedColor) return false

    // Remove if already exists
    this.data.customColors = this.data.customColors.filter(c => c !== normalizedColor)
    
    // Add to beginning
    this.data.customColors.unshift(normalizedColor)
    
    // Limit to 20 custom colors
    if (this.data.customColors.length > 20) {
      this.data.customColors = this.data.customColors.slice(0, 20)
    }

    this.saveData()
    this.notifyObservers('custom_color_added', normalizedColor)
    return true
  }

  // Remove custom color
  removeCustomColor(color) {
    const normalizedColor = this.normalizeColor(color)
    const initialLength = this.data.customColors.length
    
    this.data.customColors = this.data.customColors.filter(c => c !== normalizedColor)
    
    if (this.data.customColors.length !== initialLength) {
      this.saveData()
      this.notifyObservers('custom_color_removed', normalizedColor)
      return true
    }
    
    return false
  }

  // Get recent colors
  getRecentColors() {
    return [...this.data.recentColors]
  }

  // Add recent color
  addRecentColor(color) {
    const normalizedColor = this.normalizeColor(color)
    if (!normalizedColor) return false

    // Remove if already exists
    this.data.recentColors = this.data.recentColors.filter(c => c !== normalizedColor)
    
    // Add to beginning
    this.data.recentColors.unshift(normalizedColor)
    
    // Limit based on user setting
    const maxRecent = this.data.userSettings.maxRecentColors || 10
    if (this.data.recentColors.length > maxRecent) {
      this.data.recentColors = this.data.recentColors.slice(0, maxRecent)
    }

    if (this.data.userSettings.autoSave) {
      this.saveData()
    }
    
    this.notifyObservers('recent_color_added', normalizedColor)
    return true
  }

  // Clear recent colors
  clearRecentColors() {
    this.data.recentColors = []
    this.saveData()
    this.notifyObservers('recent_colors_cleared')
  }

  // Get user setting
  getSetting(key, defaultValue = null) {
    return this.data.userSettings[key] ?? defaultValue
  }

  // Set user setting
  setSetting(key, value) {
    this.data.userSettings[key] = value
    this.saveData()
    this.notifyObservers('setting_changed', { key, value })
  }

  // Get all settings
  getAllSettings() {
    return { ...this.data.userSettings }
  }

  // Update multiple settings
  updateSettings(settings) {
    this.data.userSettings = {
      ...this.data.userSettings,
      ...settings
    }
    this.saveData()
    this.notifyObservers('settings_updated', settings)
  }

  // Export data for backup
  exportData() {
    return {
      ...this.data,
      exportedAt: new Date().toISOString()
    }
  }

  // Import data from backup
  importData(importedData) {
    try {
      // Validate imported data structure
      if (!importedData || typeof importedData !== 'object') {
        throw new Error('Invalid data format')
      }

      // Merge with current data
      this.data = {
        ...this.data,
        ...importedData,
        version: this.data.version, // Keep current version
        importedAt: new Date().toISOString()
      }

      this.saveData()
      this.notifyObservers('data_imported', importedData)
      return true
    } catch (error) {
      console.error('Failed to import data:', error)
      return false
    }
  }

  // Reset to defaults
  reset() {
    this.data = this.constructor.prototype.loadData.call({ data: {} })
    this.saveData()
    this.notifyObservers('data_reset')
  }

  // Normalize color format (hex, rgb, etc.)
  normalizeColor(color) {
    if (!color) return null

    // Remove whitespace
    color = color.trim()

    // Handle hex colors
    if (color.startsWith('#')) {
      // Validate hex color
      if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
        // Convert 3-digit hex to 6-digit
        if (color.length === 4) {
          return '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3]
        }
        return color.toUpperCase()
      }
    }

    // Handle rgb/rgba colors
    if (color.startsWith('rgb')) {
      try {
        // Create a temporary element to get computed color
        const div = document.createElement('div')
        div.style.color = color
        document.body.appendChild(div)
        const computedColor = window.getComputedStyle(div).color
        document.body.removeChild(div)
        
        // Convert rgb to hex
        const rgb = computedColor.match(/\d+/g)
        if (rgb && rgb.length >= 3) {
          const hex = '#' + 
            parseInt(rgb[0]).toString(16).padStart(2, '0') +
            parseInt(rgb[1]).toString(16).padStart(2, '0') +
            parseInt(rgb[2]).toString(16).padStart(2, '0')
          return hex.toUpperCase()
        }
      } catch (error) {
        console.warn('Failed to normalize rgb color:', color)
      }
    }

    // Handle named colors
    try {
      const div = document.createElement('div')
      div.style.color = color
      document.body.appendChild(div)
      const computedColor = window.getComputedStyle(div).color
      document.body.removeChild(div)
      
      if (computedColor !== 'rgba(0, 0, 0, 0)') {
        return this.normalizeColor(computedColor)
      }
    } catch (error) {
      console.warn('Failed to normalize named color:', color)
    }

    return null
  }

  // Validate color format
  isValidColor(color) {
    return this.normalizeColor(color) !== null
  }

  // Get color palette for UI
  getColorPalette() {
    return {
      custom: this.getCustomColors(),
      recent: this.getRecentColors(),
      default: [
        '#000000', '#FFFFFF', '#FF0000', '#00FF00', 
        '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000',
        '#800080', '#008080', '#C0C0C0', '#808080'
      ]
    }
  }

  // Observer pattern for UI updates
  addObserver(callback) {
    this.observers.add(callback)
    return () => this.observers.delete(callback)
  }

  notifyObservers(event, data) {
    this.observers.forEach(observer => {
      try {
        observer(event, data)
      } catch (error) {
        console.error('Observer error:', error)
      }
    })
  }

  // Get statistics
  getStats() {
    return {
      customColors: this.data.customColors.length,
      recentColors: this.data.recentColors.length,
      settings: Object.keys(this.data.userSettings).length,
      version: this.data.version,
      lastModified: this.data.lastModified
    }
  }
}

// Create singleton instance
export const dataPersistence = new DataPersistence()

export default DataPersistence